import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { StatusBadge } from '@/components/common';
import type { ColDef } from '@/grid/types';
import type {
  OnFarmImplantFreshFrozen,
  OnFarmImplantSessionEmbryo,
} from '@/services/on-farm-implant';

import { embryoStatusColors, statusColors } from '../FrozenTab/columnDefs';

const freshFrozenColors: Record<
  OnFarmImplantFreshFrozen,
  { bg: string; text: string }
> = {
  Fresh: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  Frozen: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
};

export const useFlushEmbryoColumnDefs = (
  readOnly: boolean,
): ColDef<OnFarmImplantSessionEmbryo>[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        colId: 'checkbox',
        headerName: '',
        width: 44,
        maxWidth: 44,
        checkboxSelection: !readOnly,
        headerCheckboxSelection: !readOnly,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        lockPosition: true,
        filter: false,
        sortable: false,
      },
      {
        field: 'embryoName',
        headerName: t('onFarmImplant.flush.embryo'),
        flex: 1.4,
        minWidth: 150,
        filter: 'agTextColumnFilter',
        getQuickFilterText: (params: { data?: OnFarmImplantSessionEmbryo }) =>
          [params.data?.embryoName, params.data?.recordName]
            .filter(Boolean)
            .join(' '),
        cellRenderer: (params: { data?: OnFarmImplantSessionEmbryo }) => {
          if (!params.data) return null;
          return (
            <div>
              <div className="font-medium text-gray-900">
                {params.data.embryoName ?? '—'}
              </div>
              <div className="text-xs text-gray-500">
                {params.data.recordName ?? ''}
              </div>
            </div>
          );
        },
      },
      {
        field: 'accountName',
        headerName: t('onFarmImplant.flush.account'),
        flex: 1.2,
        minWidth: 130,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'donorName',
        headerName: t('onFarmImplant.flush.donor'),
        flex: 1,
        minWidth: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'sireName',
        headerName: t('onFarmImplant.flush.sire'),
        flex: 1,
        minWidth: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'freshFrozen',
        headerName: t('onFarmImplant.flush.freshFrozen'),
        flex: 0.8,
        minWidth: 110,
        filter: 'agTextColumnFilter',
        valueFormatter: (params: { value?: string | null }) =>
          params.value === 'Fresh'
            ? t('onFarmImplant.flush.fresh')
            : params.value === 'Frozen'
              ? t('onFarmImplant.flush.frozen')
              : '',
        getQuickFilterText: (params: { value?: string | null }) =>
          params.value === 'Fresh'
            ? t('onFarmImplant.flush.fresh')
            : params.value === 'Frozen'
              ? t('onFarmImplant.flush.frozen')
              : '',
        cellRenderer: (params: {
          value?: OnFarmImplantFreshFrozen | null;
          valueFormatted?: string | null;
        }) => {
          if (!params.value) return null;
          const style = freshFrozenColors[params.value];
          return (
            <span
              className={`rounded px-2 py-1 text-xs font-medium ${style.bg} ${style.text}`}
            >
              {params.valueFormatted ?? params.value}
            </span>
          );
        },
      },
      {
        field: 'frozenStrawNumber',
        headerName: t('onFarmImplant.flush.straw'),
        flex: 0.6,
        minWidth: 80,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'cane',
        headerName: t('onFarmImplant.flush.cane'),
        flex: 0.6,
        minWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'stage',
        headerName: t('onFarmImplant.flush.stage'),
        flex: 0.5,
        minWidth: 70,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'grade',
        headerName: t('onFarmImplant.flush.grade'),
        flex: 0.5,
        minWidth: 70,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'statusName',
        headerName: t('onFarmImplant.flush.status'),
        flex: 0.8,
        minWidth: 100,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { value?: string }) => {
          if (!params.value) return null;
          return <StatusBadge label={params.value} colorMap={statusColors} />;
        },
      },
      {
        field: 'embryoStatusName',
        headerName: t('onFarmImplant.flush.embryoStatus'),
        flex: 0.9,
        minWidth: 120,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { value?: string }) => {
          if (!params.value) return null;
          return (
            <StatusBadge label={params.value} colorMap={embryoStatusColors} />
          );
        },
      },
    ],
    [readOnly, t],
  );
};
